import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SignIn, useAuth as useClerkAuth } from '@clerk/clerk-react'
import { useAuth } from '../contexts/AuthContext'
import { Shield, AlertCircle, RefreshCw } from 'lucide-react'

const CLERK_LOAD_TIMEOUT_MS = 10_000

export default function LoginPage() {
  const [error, setError] = useState('')
  const [clerkLoadTimedOut, setClerkLoadTimedOut] = useState(false)
  const { loginWithClerk, user, loading: authLoading } = useAuth()
  const { isLoaded: clerkLoaded, isSignedIn, getToken } = useClerkAuth()
  const navigate = useNavigate()
  const exchanging = useRef(false)

  // Redirect if our own session is already established
  useEffect(() => {
    if (user && !authLoading) {
      navigate('/', { replace: true })
    }
  }, [user, authLoading, navigate])

  // Once Clerk confirms identity, exchange its session token for our app session
  useEffect(() => {
    if (!isSignedIn || user || exchanging.current) return

    const exchange = async () => {
      exchanging.current = true
      setError('')
      try {
        const clerkToken = await getToken()
        if (!clerkToken) throw new Error('No Clerk session token available')
        await loginWithClerk(clerkToken)
        navigate('/')
      } catch (err: any) {
        setError(err.response?.data?.message || 'Sign-in failed — this account is not authorized for admin access')
      } finally {
        exchanging.current = false
      }
    }

    exchange()
  }, [isSignedIn, user, getToken, loginWithClerk, navigate])

  useEffect(() => {
    if (clerkLoaded) {
      setClerkLoadTimedOut(false)
      return
    }

    const timeoutId = window.setTimeout(() => {
      setClerkLoadTimedOut(true)
    }, CLERK_LOAD_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [clerkLoaded])

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Checking authentication...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <div className="mx-auto h-12 w-12 flex items-center justify-center rounded-full bg-blue-100">
            <Shield className="h-8 w-8 text-blue-600" />
          </div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            CheckFirst Admin Portal
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Restricted access — authorized administrators only
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <AlertCircle className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">{error}</h3>
              </div>
            </div>
          </div>
        )}

        <div className="flex min-h-[420px] justify-center">
          {!clerkLoaded ? (
            <div
              className="flex w-full max-w-sm items-center justify-center"
              role="status"
              aria-live="polite"
            >
              {clerkLoadTimedOut ? (
                <div className="w-full rounded-xl border border-amber-200 bg-white p-6 text-center shadow-sm">
                  <AlertCircle className="mx-auto h-8 w-8 text-amber-500" aria-hidden="true" />
                  <h3 className="mt-3 text-base font-semibold text-gray-900">
                    Secure sign-in could not load
                  </h3>
                  <p className="mt-2 text-sm text-gray-600">
                    Check your connection, then retry the Clerk sign-in service.
                  </p>
                  <button
                    type="button"
                    onClick={() => window.location.reload()}
                    className="mt-5 inline-flex items-center justify-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <RefreshCw className="h-4 w-4" aria-hidden="true" />
                    Retry secure sign-in
                  </button>
                </div>
              ) : (
                <div className="text-center">
                  <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-blue-100 border-b-blue-600" />
                  <p className="mt-4 text-sm font-medium text-gray-700">Loading secure sign-in…</p>
                  <p className="mt-1 text-xs text-gray-500">Connecting to Clerk</p>
                </div>
              )}
            </div>
          ) : (
            <SignIn routing="virtual" signUpUrl={undefined} />
          )}
        </div>
      </div>
    </div>
  )
}
